import qs from 'qs';
import { useEffect } from 'react';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import getUserAgent from '../../flows/purchase/functions/userAgent';
import { OnboardingStateManager } from '../../flows/user/functions/onboardingState';
import { LinksApi } from '../../generated/api';
import AsyncStorage, { Key } from '../../utils/asyncStorage';
import { makeDebug } from '../../utils/makeDebug';
import { currentUserId } from '../auth/utils';
import { isDebugEnabled } from '../debugging';
import useBehaviorSubject, {
  useBehaviorSubjectUpdater,
} from '../hooks/useBehaviorSubject';
import { usePersistFn } from '../hooks/usePersistFn';
import { useServiceFactory } from '../services';
import linkService from '../services/link.service';

const debug = makeDebug('campaign-tracker', isDebugEnabled('campaignTracker'));

// keeps track of executed campaign link ids by user id
const kExecutedCampaignIdsByUser = new Map<string, Set<string>>();

const savedCampaignId$ = AsyncStorage.behaviorSubject$<string>(
  Key.CampaignPersistent,
);

export function clearSavedCampaignId() {
  savedCampaignId$.next(null);
}

export const useCampaignRestoreFromLaunch = () => {
  const reportCampaignId = useReportCampaignId();

  useEffect(() => {
    if (currentUserId() == null) {
      return;
    }
    void getSavedShareLinkId().then(async cid => {
      if (cid == null) return;
      savedCampaignId$.next(null);
      debug.info('✅ saved campaign id cleared on launch');
      await reportCampaignId(cid);
    });
  }, [reportCampaignId]);
};

export const getSavedShareLinkId = async () => {
  return await firstValueFrom(
    savedCampaignId$.pipe(filter(a => a !== undefined)),
  );
};

export const useCurrentShareLinkId = () => useCurrentShareLink()[0];

export const useCurrentShareLink = () => {
  return useBehaviorSubject(savedCampaignId$);
};

export const useCampaignTracker = (campaignId: undefined | string) => {
  const reportCampaignId = useReportCampaignId();
  const setLinkId = useBehaviorSubjectUpdater(savedCampaignId$);

  useEffect(() => {
    if (campaignId == null) return;
    if (currentUserId() == null) {
      setLinkId(campaignId);
      debug.info('✅ saved current campaign id', campaignId);
      OnboardingStateManager.shared.appLaunchedWithCampaign(campaignId);
    } else {
      void reportCampaignId(campaignId);
    }
  }, [setLinkId, campaignId, reportCampaignId]);
  return null;
};

export const useReportCampaignId = () => {
  const { linkService } = useServiceFactory();

  return usePersistFn(async (campaignId: string): Promise<void> => {
    if (currentUserId() == null) return;
    void executeLinkAction(linkService, campaignId);
  });
};

export const utmParameters$ = new BehaviorSubject<Record<string, any>>({});

export function tryCaptureCampaignId(pathOrUrl: string | undefined) {
  if (!pathOrUrl) return;
  const index = pathOrUrl.indexOf('?');
  if (index >= 0) {
    const query = pathOrUrl.substring(index + 1);
    const params = qs.parse(query);
    const linkId = params['c'];

    if (typeof linkId === 'string' && linkId) {
      debug.success('✅ captured link id "%s" from url %s', linkId, pathOrUrl);
      savedCampaignId$.next(linkId);
      void executeLinkAction(linkService, linkId);
    }

    if (params['utm_campaign']) {
      const utm_params = Object.fromEntries(
        Object.keys(params)
          .filter(x => x.startsWith('utm_') && typeof params[x] === 'string')
          .map(k => [k, params[k] as string]),
      );
      utmParameters$.next(utm_params);
      debug('updated utm parameters: %O', params);
    }
  }
}

async function executeLinkAction(linkService: LinksApi, linkId: string) {
  const userId = currentUserId();
  if (userId == null) {
    debug.info('User is not logged in, execution of link deferred');
    return;
  }
  let set = kExecutedCampaignIdsByUser.get(userId);
  if (set == null) {
    set = new Set();
    kExecutedCampaignIdsByUser.set(userId, set);
  }

  if (set.has(linkId)) {
    debug.verbose('campaign link %s already executed, skipped', linkId);
    return;
  }

  set.add(linkId);

  try {
    debug.verbose('report & execute action for campaign id: %s', linkId);
    await linkService.linksApiControllerExecuteAction({
      linkId,
      executeActionRequest: {
        userAgent: getUserAgent(),
      },
    });
    debug.success('✅ link %s execution successful', linkId);
  } catch (e) {
    set.delete(linkId);
    console.error('failed to execute link action: ', e);
  }
}
